import "./GlobalJobsAccountCompact.css";
import React, { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "./config";
import "./GlobalJobsCandidateAccount.css";
import GlobalJobsMyApplications from "./GlobalJobsMyApplications";
import GlobalJobsMembership from "./GlobalJobsMembership";
import GlobalJobsCandidateProfile from "./GlobalJobsCandidateProfile";

const STORAGE_KEY = "gtj_candidate_session_v1";

function getSession() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function saveSession(token) {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Account still works for this browser session.
  }
}

function clearSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore unavailable browser storage.
  }
}

async function apiRequest(path, options = {}) {
  const response = await fetch(
    `${API_BASE_URL}/global-tech-jobs/auth/${path}`,
    options
  );

  let data = {};
  try {
    data = await response.json();
  } catch {
    // Handle missing or non-JSON responses below.
  }

  if (!response.ok) {
    const detail = data.detail;
    throw new Error(
      typeof detail === "string"
        ? detail
        : `Request failed (${response.status}).`
    );
  }

  return data;
}

export default function GlobalJobsCandidateAccount() {
  const [token, setToken] = useState(getSession);
  const [candidate, setCandidate] = useState(null);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email");
  const [open, setOpen] = useState(false);

  // GTJ COMPACT CANDIDATE ACCOUNT V1
  const [dashboardOpen, setDashboardOpen] = useState(false);

  useEffect(() => {
    const listener = () => {
      if (!token) {
        setOpen(true);
      } else {
        setDashboardOpen(true);
      }
    };

    window.addEventListener(
      "gtj-open-candidate-login",
      listener
    );

    return () => {
      window.removeEventListener(
        "gtj-open-candidate-login",
        listener
      );
    };
  }, [token]);
  useEffect(() => {
    const handleApply = () => {
      if (!getSession()) {
        setOpen(true);
      }
    };

    window.addEventListener("gtj-apply-login", handleApply);
    return () =>
      window.removeEventListener(
        "gtj-apply-login", handleApply
      );
  }, []);

  useEffect(() => {
    if (token) {
      window.dispatchEvent(
        new Event("gtj-candidate-authenticated")
      );
    }
  }, [token]);


  // GTJ UNIFIED APPLICATION WIZARD V1
  useEffect(() => {
    const syncWizardLogin = () => {
      const current = getSession();
      if (current) {
        setToken(current);
        setOpen(false);
      }
    };
    window.addEventListener("gtj-wizard-login", syncWizardLogin);
    return () =>
      window.removeEventListener("gtj-wizard-login", syncWizardLogin);
  }, []);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadProfile = useCallback(async (accessToken) => {
    try {
      const data = await apiRequest("me", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setCandidate(data.candidate || null);
    } catch {
      clearSession();
      setToken("");
      setCandidate(null);
    }
  }, []);

  useEffect(() => {
    if (token) loadProfile(token);
  }, [token, loadProfile]);

  const requestOtp = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await apiRequest("request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
        }),
      });

      setStep("otp");
      setMessage(
        "If the email was accepted, your verification code is on its way."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError("");

    try {
      const data = await apiRequest("verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: otp.trim(),
        }),
      });

      if (!data.access_token || !data.candidate) {
        throw new Error("Incomplete login response.");
      }

      saveSession(data.access_token);
      setCandidate(data.candidate);
      setToken(data.access_token);
      setOtp("");
      setOpen(false);
      setMessage("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    setBusy(true);

    try {
      if (token) {
        await apiRequest("logout", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch {
      // Clear local session even if server cannot be reached.
    } finally {
      clearSession();
      setToken("");
      setCandidate(null);
      setStep("email");
      setEmail("");
      setOtp("");
      setMessage("");
      setError("");
      setBusy(false);
    }
  };

  return (
    <section className="gtja-account">

      {/* GTJ APPLY FIRST LOGIN V23 */}
      {candidate && (
        <div className="gtja-compact-entry gtja-logged-in-entry">
          <div className="gtja-compact-icon" aria-hidden="true">
            &#9678;
          </div>

          <div className="gtja-compact-copy">
            <strong>My Job Dashboard</strong>
            <span>
              {candidate.full_name || candidate.email}
              {" · "}
              Profile, membership and application tracking
            </span>
          </div>

          <button
            type="button"
            className="gtja-compact-action"
            onClick={() => setDashboardOpen((value) => !value)}
            aria-expanded={dashboardOpen}
          >
            {dashboardOpen
              ? "Hide Dashboard"
              : "My Job Dashboard"}
          </button>
        </div>
      )}

      {!candidate ? (
        <div className="gtja-banner">
          <div>
            <span>YOUR GLOBAL TECH JOBS ACCOUNT</span>
            <h3>Track your next career opportunity.</h3>
            <p>
              Create your separate candidate account using your
              email address. Your job applications and annual
              membership will appear here when activated.
            </p>
          </div>
          <button type="button" onClick={() => setOpen(true)}>
            Candidate Login / Register
          </button>
        </div>
      ) : (
        <div className="gtja-dashboard" hidden={!dashboardOpen}>
          <div className="gtja-dashboard-top">
            <div>
              <span>CANDIDATE DASHBOARD</span>
              <h3>
                Welcome{candidate.full_name
                  ? `, ${candidate.full_name}`
                  : " to Global Tech Jobs"}
              </h3>
              <p>{candidate.email}</p>
              <small>Candidate ID: {candidate.candidate_id}</small>
            </div>
            <button type="button" onClick={logout} disabled={busy}>
              Logout
            </button>
          </div>

          {/* Live membership, profile and applications
                are shown below instead of stale status cards. */}
          <GlobalJobsMembership token={token} candidate={candidate} />
          <GlobalJobsCandidateProfile token={token} />
          <GlobalJobsMyApplications token={token} />

          <p className="gtja-note">
            Your profile, membership and submitted applications
            can be managed from this dashboard.
          </p>
        </div>
      )}

      {open && !candidate && (
        <div
          className="gtja-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setOpen(false);
            }
          }}
        >
          <div
            className="gtja-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Candidate login"
          >
            <button
              className="gtja-close"
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close candidate login"
            >
              ×
            </button>

            <span>GLOBAL TECH JOBS</span>

            {step === "email" ? (
              <form onSubmit={requestOtp}>
                <h3>Login or Create Your Account</h3>
                <p>
                  Enter your email address. We'll send a
                  one-time verification code.
                </p>
                <label>
                  Email Address
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    maxLength={254}
                  />
                </label>
                <button type="submit" disabled={busy}>
                  {busy ? "Sending..." : "Send Verification Code"}
                </button>
              </form>
            ) : (
              <form onSubmit={verifyOtp}>
                <h3>Verify Your Email</h3>
                <p>Enter the six-digit code sent to {email}.</p>
                <label>
                  Verification Code
                  <input
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    maxLength={6}
                    pattern="[0-9]{6}"
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="000000"
                    autoComplete="one-time-code"
                    required
                  />
                </label>
                <button type="submit" disabled={busy || otp.length !== 6}>
                  {busy ? "Verifying..." : "Verify and Login"}
                </button>
                <button
                  className="gtja-link"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setStep("email");
                    setOtp("");
                    setError("");
                    setMessage("");
                  }}
                >
                  Change email / Request another code
                </button>
              </form>
            )}

            {error && <p className="gtja-error">{error}</p>}
            {message && <p className="gtja-message">{message}</p>}

            <small>
              Your email is used to securely access your job applications.
            </small>
          </div>
        </div>
      )}
    </section>
  );
}
